import { useCallback, useEffect, useRef, useState } from 'react';
import { useTracking } from 'react-tracking';
import Media from 'react-media';
import { debounce } from 'lodash';
import { Transition } from 'react-transition-group';

import {
  AngledCollapseSolid,
  AngledExpandSolid,
  Button,
  CrossSolid,
  Grid,
  NotificationOutline,
  useTheme,
  usePreventWindowScroll,
  Box,
  Text,
} from '@simplywallst/ui-core';

import { useUserAgent } from '@simplywallst/hooks/useUserAgent';
import { useFlag } from '@/features/unleash';
import { useQueryStringParam } from '@/hooks/useQueryStringParam';

import ActionSheet, { Sheet, Background } from '@/components/ActionSheet';
import type { Content } from '@/components/RichTextEditor/RichTextEditor';
import type { NoteFragmentType } from '@components/Note';
import FlaggedNoteReminder from '@/components/NoteReminder/components/NoteReminder';

import NotesRichTextEditor from '../NotesRichTextEditor';
import { useCreateCompanyNote } from '../../hooks/useCreateCompanyNote';
import { useUpdateCompanyNote } from '../../hooks/useUpdateCompanyNote';

import {
  Animation,
  StyledGrid,
  StyledPanel,
  TransitionWrapper,
} from './styled';

interface Props {
  uniqueSymbol?: string;
  companyId?: string;
  note: NoteFragmentType | undefined;
  setNote: (note: NoteFragmentType | undefined | null) => void;
  onClose: () => void;
}

const PreventWindowScroll = () => {
  usePreventWindowScroll();
  return null;
};

function handleModeQueryParam(
  modeQueryParam: string | null
): 'full' | 'compact' {
  if (modeQueryParam !== 'full' && modeQueryParam !== 'compact') {
    return 'compact';
  }

  if (modeQueryParam === null) {
    return 'compact';
  }

  return modeQueryParam;
}

export const SaveCompanyNoteModal = ({
  companyId,
  note,
  setNote,
  onClose,
  uniqueSymbol,
}: Props) => {
  // @TODO: refactor get modal mode into a separate hook: @return mode & toggleMode
  const flag = useFlag({ name: 'note-compact-modal' });
  const COMPACT_MODAL_ENABLED = flag.enabled;
  const { isMobile, isDesktop, isTablet } = useUserAgent();
  const { getItem, removeItem, setItem } = useQueryStringParam();
  const modeQueryParam = getItem('mode');
  const mode = handleModeQueryParam(modeQueryParam);
  const [currentMode, setCurrentMode] = useState<'full' | 'compact'>(mode);
  useEffect(() => {
    if (COMPACT_MODAL_ENABLED && modeQueryParam === null) {
      setCurrentMode('compact');
    }
  }, [COMPACT_MODAL_ENABLED, mode]);
  useEffect(() => {
    if (currentMode !== mode && modeQueryParam !== null) {
      setItem('mode', currentMode);
    }
  }, [currentMode]);

  const [defaultContent, setDefaultContent] = useState<string | undefined>(
    note?.content
  );
  const [showNoteReminder, setShowNoteReminder] = useState(false);
  const noteReminderFeatureFlag = useFlag({ name: 'mono-note-reminder' });
  const isNoteReminderFeatureEnabled =
    noteReminderFeatureFlag.flagsReady && noteReminderFeatureFlag.enabled;

  const metaDataRef = useRef<{
    noteId: string | undefined;
    currentContent: string | undefined;
    companyId: string | undefined;
    action: 'viewed' | 'created' | 'edited';
    type: 'create' | 'update';
  }>({
    noteId: note?.id,
    currentContent: note?.content,
    companyId,
    action: note ? 'viewed' : 'created',
    type: note ? 'update' : 'create',
  });

  useEffect(() => {
    const metaData = metaDataRef.current;
    if (note?.id !== metaData.noteId) {
      metaDataRef.current.noteId = note?.id;
      metaDataRef.current.type = note ? 'update' : 'create';
      metaDataRef.current.action = 'viewed';
      metaDataRef.current.currentContent = note?.content;
      setDefaultContent(note?.content);
    }
    if (companyId !== metaData.companyId) {
      metaDataRef.current.companyId = companyId;
    }
  }, [note, companyId]);

  useEffect(() => {
    const metaData = metaDataRef.current;
    if (typeof metaData?.currentContent !== 'undefined') {
      setDefaultContent(metaData.currentContent);
    }
  }, [showNoteReminder]);

  const [isOnline, setIsOnline] = useState(true);

  const theme = useTheme();
  const { Track, trackEvent } = useTracking({
    type: 'track',
    subject: 'note',
    noteId: note?.id,
    companyId,
  });

  const [open, setOpen] = useState(true);
  const [editorError, setEditorError] = useState<string | null>(null);

  const {
    mutate: createNote,
    isError: isCreateError,
    isSuccess: isCreationSuccess,
    isLoading: isCreationLoading,
    reset,
  } = useCreateCompanyNote({
    onSuccess: (data?: NoteFragmentType) => {
      setNote(data);
      metaDataRef.current.noteId = data?.id;
    },
  });
  const {
    mutate: updateCompanyNote,
    isError: isUpdateError,
    isLoading: isUpdateLoading,
    isSuccess: isUpdateSuccess,
  } = useUpdateCompanyNote();

  const resetError = useCallback(() => {
    setEditorError(null);
    if (isCreateError || isUpdateError) {
      reset();
    }
  }, [isCreateError, isUpdateError, reset]);

  const debouncedSaveData = debounce(
    (
      content: Content,
      currentNoteId: string | undefined,
      companyId: string | undefined
    ) => {
      if (!content.text || !companyId) {
        return;
      }
      if (currentNoteId && content?.html) {
        updateCompanyNote({
          companyId,
          id: currentNoteId,
          content: content.html,
        });
      }
      if (!currentNoteId && content?.html) {
        createNote({
          companyId,
          content: content.html,
        });
      }
    },
    1000
  );

  const handleUpdate = (content: Content) => {
    const metaData = metaDataRef.current;
    metaDataRef.current.action =
      metaData.type === 'update' ? 'edited' : 'created';
    metaDataRef.current.currentContent = content.html;
    debouncedSaveData(content, metaData.noteId, metaData.companyId);
  };

  const closeEditor = () => {
    setOpen(false);
  };

  const handleOnClose = (): void => {
    removeItem('note');
    removeItem('mode');
    onClose();
  };

  useEffect(() => {
    if (RUNTIME_ENV === 'native') {
      return;
    }
    // Update network status
    const handleStatusChange = () => {
      const metaData = metaDataRef.current;
      if (
        navigator.onLine &&
        metaData.currentContent &&
        metaData.noteId &&
        metaData.companyId
      ) {
        resetError();
        updateCompanyNote({
          companyId: metaData.companyId,
          id: metaData.noteId,
          content: metaData.currentContent,
        });
      }
      if (!navigator.onLine) {
        setEditorError(
          'Looks like you are offline. Your changes will be synced once you are back online'
        );
      }
      setIsOnline(navigator.onLine);
    };

    window.addEventListener('online', handleStatusChange);
    window.addEventListener('offline', handleStatusChange);

    // Cleanup
    return () => {
      window.removeEventListener('online', handleStatusChange);
      window.removeEventListener('offline', handleStatusChange);
    };
  }, [isOnline, resetError, updateCompanyNote]);

  const getErrorMessage = (): string =>
    isCreateError || isUpdateError
      ? `Failed to ${isCreateError ? 'create' : 'update'} note`
      : '';

  const mutationError = getErrorMessage();

  return (
    <Track>
      <Media
        queries={{
          mobile: { maxWidth: theme.smMaxRaw },
          tablet: { minWidth: theme.mdMinRaw, maxWidth: theme.mdMaxRaw },
          desktop: { minWidth: theme.lgMinRaw },
        }}
        defaultMatches={{
          mobile: isMobile,
          tablet: isTablet,
          desktop: isDesktop,
        }}
      >
        {(matches) => {
          const showingCompactMode =
            currentMode === 'compact' && !(matches.mobile || matches.tablet);

          const generateTemplateColumns = () => {
            if (
              matches.desktop &&
              COMPACT_MODAL_ENABLED &&
              isNoteReminderFeatureEnabled
            ) {
              return `${theme.x5spacePx}px auto ${theme.x5spacePx}px ${theme.x5spacePx}px`;
            }

            if (
              matches.desktop &&
              COMPACT_MODAL_ENABLED &&
              !isNoteReminderFeatureEnabled
            ) {
              return `${theme.x5spacePx}px auto ${theme.x5spacePx}px`;
            }

            if (!matches.desktop && isNoteReminderFeatureEnabled) {
              return `auto ${theme.x5spacePx}px ${theme.x5spacePx}px`;
            }

            if (!matches.desktop && !isNoteReminderFeatureEnabled) {
              return `auto ${theme.x5spacePx}px`;
            }

            return `auto ${theme.x5spacePx}px`;
          };

          return (
            <ActionSheet
              open={open}
              onClose={() => setOpen(false)}
              onClosed={() => {
                trackEvent({
                  action: metaDataRef.current.action,
                  modifier: 'dismiss',
                });
                handleOnClose();
              }}
            >
              {!showingCompactMode && (
                <>
                  <PreventWindowScroll />
                  <Background onClick={() => setOpen(false)} />
                </>
              )}
              <Sheet
                zIndex={showingCompactMode ? 100 : 9999}
                sheetLayout={
                  showingCompactMode
                    ? {
                        right: '40px',
                        'max-width': '443px',
                        height: '430px',
                      }
                    : {
                        top: `${!isDesktop ? 0 : 'auto'}`,
                        right: 'max(0px, calc(50vw - 443px))',
                        'max-width': '886px',
                        height: `calc(100dvh - ${theme.safeAreaInsetTop} - ${theme.safeAreaInsetBottom})`,
                      }
                }
              >
                <Grid
                  templateRows={`calc(40px + ${theme.safeAreaInsetTop}) auto`}
                >
                  <StyledPanel
                    templateRows="max-content 1fr"
                    row="2"
                    isCompactMode={showingCompactMode}
                  >
                    <StyledGrid
                      templateColumns={generateTemplateColumns()}
                      alignItems="center"
                      isDesktop={matches.desktop}
                    >
                      {matches.desktop && COMPACT_MODAL_ENABLED && (
                        <Grid justifyContent="center" alignItems="center">
                          <Button
                            type="button"
                            styleType="passive"
                            aria-label="Expand"
                            data-cy-id="action-sheet-expand"
                            onClick={() => {
                              trackEvent({
                                action: 'click',
                                modifier: `create${
                                  currentMode === 'full' ? 'Collapse' : 'Expand'
                                }`,
                              });
                              setCurrentMode(
                                currentMode === 'full' ? 'compact' : 'full'
                              );
                            }}
                          >
                            {currentMode === 'compact' ? (
                              <AngledExpandSolid />
                            ) : (
                              <AngledCollapseSolid />
                            )}
                          </Button>
                        </Grid>
                      )}
                      <Grid justifyContent="flex-start" alignItems="center">
                        <Box paddingLeft="x1">
                          <Text typography="xSmall" color="mediumGray">
                            {uniqueSymbol || ''}
                          </Text>
                        </Box>
                      </Grid>
                      {isNoteReminderFeatureEnabled && (
                        <Grid justifyContent="center" alignItems="center">
                          <Button
                            type="button"
                            styleType={showNoteReminder ? 'loud' : 'passive'}
                            onClick={() =>
                              setShowNoteReminder(!showNoteReminder)
                            }
                            disabled={typeof note === 'undefined'}
                          >
                            <NotificationOutline />
                          </Button>
                        </Grid>
                      )}
                      <Grid justifyContent="center" alignItems="center">
                        <Button
                          type="button"
                          styleType="passive"
                          onClick={() => setOpen(false)}
                          aria-label="Close"
                          data-cy-id="action-sheet-close"
                        >
                          <CrossSolid />
                        </Button>
                      </Grid>
                    </StyledGrid>
                    <TransitionWrapper>
                      <Transition
                        in={!showNoteReminder}
                        timeout={500}
                        mountOnEnter
                        unmountOnExit
                      >
                        {(state) => (
                          <Animation
                            isEnter={
                              state === 'entering' || state === 'entered'
                            }
                          >
                            <NotesRichTextEditor
                              defaultContent={defaultContent}
                              errorMessage={editorError || mutationError}
                              isLoading={isCreationLoading || isUpdateLoading}
                              isSuccess={
                                !(isCreationLoading || isUpdateLoading) &&
                                (isCreationSuccess || isUpdateSuccess)
                              }
                              onDimissAlert={resetError}
                              onError={(error) => setEditorError(error)}
                              onUpdate={handleUpdate}
                              onSave={closeEditor}
                              saveButtonText="Close"
                            />
                          </Animation>
                        )}
                      </Transition>
                      <Transition
                        in={showNoteReminder}
                        timeout={500}
                        mountOnEnter
                        unmountOnExit
                      >
                        {(state) => (
                          <Animation
                            isEnter={
                              state === 'entering' || state === 'entered'
                            }
                          >
                            {companyId && note && (
                              <Grid
                                templateRows="max-content max-content auto"
                                gap={`${theme.x1spacePx * 1.5}px`}
                              >
                                <FlaggedNoteReminder
                                  companyId={companyId}
                                  noteId={note.id}
                                  goBack={() => setShowNoteReminder(false)}
                                />
                              </Grid>
                            )}
                          </Animation>
                        )}
                      </Transition>
                    </TransitionWrapper>
                  </StyledPanel>
                </Grid>
              </Sheet>
            </ActionSheet>
          );
        }}
      </Media>
    </Track>
  );
};
